import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useCoveredEligible } from '@sentara-health/sentara-api-hooks-core';
import { toSentancePascalCase, DeviceTypes, useDeviceType, internalServerErrorCode, CommonErrorMessage, formatISODateToMDY } from 'common';
import ApiWrapper from '../../common/ApiWrapper';
import {
  Headings,
  Accordion,
  ContactInfo,
  Table,
  Button,
  Paragraph,
  SortArrowIcon
} from '@sentara-health/optimahealth_web';
import { GlobalContext } from 'context';


const CoveredEligibleMembers = () => {
  const { t } = useTranslation();
  const [coveredMembers, setCoveredMembers] = useState<any>();
  const { CoveredEligibleType } = useCoveredEligible();
  const [phone, setPhone] = useState<any>();
  const { data, errorMessage, callApi }: any = ApiWrapper(CoveredEligibleType);
  const [showMessage, setShowMessage] = useState('');
  const {
    serviceNumber
  } = useContext(GlobalContext);

  // getting the phone number from the local storage and CoverEligible api
  async function asyncCall() {
    callApi()
    if (serviceNumber) {
      setPhone(JSON.parse(serviceNumber ?? ''));
    }
  }


  // getting the data or error message from the api
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      const filterEnrollmentCode = "E";
      type Member = {
        enrollmentCode: string;
      };
      const filteredData = data.members.filter((member: Member) => member['enrollmentCode'] === filterEnrollmentCode);
      if (filteredData.length > 0) {
        setCoveredMembers(filteredData);
      } else {
        setShowMessage(t('common.noResults') ?? '');
      }
    } else if (errorMessage === Number(internalServerErrorCode)) {
      setShowMessage(t('common.internalServerError') ?? '')
    }
  }, [data, errorMessage]);


  useEffect(() => {
    (async () => {
      await asyncCall();
    })();
  }, []);

  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortedMembers = coveredMembers?.sort((a: any, b: any) => {
    const valueA = a[sortColumn];
    const valueB = b[sortColumn];

    if (valueA < valueB) {
      return sortOrder === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return sortOrder === 'asc' ? 1 : -1;
    }
    return 0;
  });
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  interface Member {
    memberID: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactFragment | React.ReactPortal | null | undefined;
    fullName: string;
    effectiveDate: string;
    dob: string;
  }

  const SortedMembersRows = (sortedMembers: Member[]) => {
    return sortedMembers?.map((item: Member, index: number) => {
      return (
        <tr key={`${item.memberID}`}>
          <th scope='col' data-testid={`coveredMemberName_${index}`}>
            {toSentancePascalCase(item.fullName)}
          </th>
          <td data-testid={`coveredMemberId_${index}`}>
            {item.memberID}
          </td>
          <td data-testid={`coveredMemberEffectiveDate_${index}`}>
            {formatISODateToMDY(item?.effectiveDate)}
          </td>
          <td data-testid={`coveredMemberdob_${index}`}>
            {formatISODateToMDY(item?.dob)}
          </td>
        </tr>
      );
    });
  }

  const headerRows = [
    {
      text: (
        <Button
          dataTestId="coveredMemberNameButton"
          className="white-color bg-transparent border-0 p-0 m-0"
          onClick={() => handleSort('fullName')}
          iconRight={<SortArrowIcon />}
        >{t('common.memberName')}

        </Button>
      )
    },
    {
      text: (
        <Button
          dataTestId="coveredMemberIdButton"
          className="white-color bg-transparent border-0 p-0 m-0"
          onClick={() => handleSort('memberID')}
          iconRight={<SortArrowIcon />}
        >{t('common.memberId')}</Button>

      )
    },
    {
      text: (
        <Button
          dataTestId="coveredMemberEffectiveDateButton"
          className="white-color bg-transparent border-0 p-0 m-0"
          onClick={() => handleSort('effectiveDate')}
          iconRight={<SortArrowIcon />}
        >{t('common.effectiveDate')}</Button>
      )
    },
    {
      text: (
        <Button
          dataTestId="coveredMemberDobButton"
          className="white-color bg-transparent border-0 p-0 m-0"
          onClick={() => handleSort('dob')}
          iconRight={<SortArrowIcon />}
        >{t('common.dateOfBirth')}</Button>
      )
    },
  ];

  return (
    <>
      <div className="mb-4">
        <Headings
          level={1}
          text={t('common.coveredEligibleMembers')}
          variant="semiBold"
          dataTestId="coveredEligibleMembersHeader"
        />
      </div>
      {!isMobile && sortedMembers?.length > 0 && (
        <Table className="coverage-table mb-4" header={headerRows} rowsChildren={SortedMembersRows(sortedMembers)} />
      )}

      {/* To show the internalServerError message */}
      <CommonErrorMessage message={showMessage} />

      {isMobile &&
        (sortedMembers?.length > 0 ? (
          <div className="accordion-table mb-4">
            <Accordion
              initialActiveIndex={0}
              items={
                sortedMembers?.length > 0 &&
                sortedMembers?.map((item: any, i: any) => ({
                  title: (
                    <div data-testid={`authNumberTab${i}`}>
                      <span className="font-sixteen">
                        {toSentancePascalCase(item.fullName)}
                      </span>
                    </div>
                  ),
                  content: (
                    <div>
                      <div
                        data-testid={`coveredMemberId${i}`}
                      >
                        {t('common.memberId')}
                      </div>
                      <div
                        data-testid={`coveredMemberId_${i}`}
                        className="mb-3"
                      >
                        {item.memberID}
                      </div>
                      <div
                        data-testid={`coveredEffectiveDate${i}`}
                      >
                        {t('common.effectiveDate')}
                      </div>
                      <div
                        data-testid={`coveredMemberEffectiveDate_${i}`}
                        className="mb-3"
                      >
                        {formatISODateToMDY(item.effectiveDate)}
                      </div>
                      <div
                        data-testid={`coveredMemberDob${i}`}
                      >
                        {t('common.dateOfBirth')}
                      </div>
                      <div
                        data-testid={`coveredMemberdob_${i}`}
                        className="mb-3"
                      >
                        {formatISODateToMDY(item.dob)}
                      </div>
                    </div>
                  ),
                }))
              }
            />
          </div>
        ) : (
          <div className="mb-4">
            <div className="no-results text-center">{t('common.noResults')}</div>
          </div>
        ))}

      {phone !== undefined && phone?.data !== '' && (
        <div className="mb-5">
          <Paragraph text={
            <>
              {t('common.questions')}{' '}
              <ContactInfo
                phoneData={{
                  phone1800: phone?.data?.benefitPhone1800,
                  phoneLocal: phone?.data?.benefitPhoneLocal,
                }}
              /></>} variant="regular" data-testid="contactInfo" />

        </div>
      )}
    </>
  );
};

export default CoveredEligibleMembers;